import { forwardRef, useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useUID } from "react-uid";
import useBrandContext from "Contexts/brand-context";
import InlineSvg from "Shared/components/inline-svg";
import { useTranslation } from "Shared/util/custom-hooks";
import {
    isValidFinnishSocialSecurityNumber,
    isValidSwedishSocialSecurityNumber,
} from "Shared/util/social-security-number";
import { BrandName } from "Shared/util/types";
import styles from "./input-text.css";

const MAX_LENGTH_FINNISH_SSN = 11;
const MAX_LENGTH_SWEDISH_SSN = 12;

// TODO (later) might be handled in backend instead?
const formatFinishSSN = (socialSecurityNumber) =>
    socialSecurityNumber?.replace(/\s+/g, "");

const formatSwedishSSN = (socialSecurityNumber) =>
    socialSecurityNumber?.replace(/\D/g, "");
//***/

const InputText = forwardRef(
    (
        {
            value,
            onChange,
            className,
            inputType,
            label: customLabel,
            labelClassName,
            placeholder,
            setEnabledSubmit,
            isRequired,
            compareValue,
            width,
            onClick,
            onKeyDown,
            isReadOnly,
            isDisabled,
            name,
            compareFunc,
            skipValidation
        },
        ref
    ) => {
        const { brandData } = useBrandContext();
        const t = useTranslation((x) => x.inputText);
        const tg = useTranslation((x) => x.global);
        const uid = useUID();

        const [typeField, setTypeField] = useState("text");
        const [placeholderField, setPlaceholderField] = useState(placeholder);
        const [autoComplete, setAutoComplete] = useState(undefined);
        const [label, setLabel] = useState(customLabel);
        const [validationStyle, setValidationStyle] = useState(undefined);
        const [isFocused, setIsFocused] = useState(undefined);
        const [errorMsg, setErrorMsg] = useState();
        const [maxLength, setMaxLength] = useState(undefined);

        const isFinanceLink = brandData?.name === BrandName.FinanceLink;
        const noInput = !value;

        const inputIsValid = () => {
            if (inputType === "email") {
                // eslint-disable-next-line no-useless-escape
                const regExp =
                    // eslint-disable-next-line no-useless-escape
                    /((([A-Za-z]{3,9}:(?:\/\/)?)(?:[\-;:&=\+\$,\w]+@)?[A-Za-z0-9\.\-]+|(?:www\.|[\-;:&=\+\$,\w]+@)[A-Za-z0-9\.\-]+)((?:\/[\+~%\/\.\w\-_]*)?\??(?:[\-\+=&;%@\.\w_]*)#?(?:[\.\!\/\\\w]*))?)/;
                return value.match(regExp);
            }
            if (inputType === "socialSecurityNumber") {
                return isFinanceLink
                    ? isValidFinnishSocialSecurityNumber(value)
                    : isValidSwedishSocialSecurityNumber(value);
            }
            if (inputType === "new-password") {
                const regExp = /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.{10,})/;
                return value.match(regExp);
            }
            /* if (inputType === 'current-password') {
                const regExp = /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.{10,})/;
                return value.match(regExp);
            } */
            if (inputType === "confirm-password") {
                return compareValue && value === compareValue;
            }
            if (inputType === "account-category") {
                return (
                    !noInput &&
                    value.toString().length === 4 &&
                    compareFunc(value) <= 1
                );
            }
            return true;
        };

        const validInput = value && inputIsValid();
        const invalidInput = value && !inputIsValid();

        const setErrorMessage = () => {
            switch (inputType) {
                case "email":
                    setErrorMsg(t.invalidEmailAddress);
                    break;
                case "socialSecurityNumber":
                    setErrorMsg(t.invalidSocialSecurityNumber);
                    break;
                case "new-password":
                    setErrorMsg(t.passwordFormatErrorMsg);
                    break;
                case "confirm-password":
                    setErrorMsg(t.passwordsProvidedDoNotMatch);
                    break;
                case "account-category":
                    setErrorMsg(t.accountIsBusy);
                    break;
                default:
                    setErrorMsg(t.invalidValue);
                    break;
            }
        };

        const clearErrorMessage = () => {
            setErrorMsg("");
        };

        const handleEnabledSubmit = (bool) => {
            if (setEnabledSubmit) setEnabledSubmit(bool);
        };

        useEffect(() => {
            if (noInput || skipValidation === true) {
                setValidationStyle(undefined);
                clearErrorMessage();
                handleEnabledSubmit(!isRequired);
            } else if (validInput) {
                setValidationStyle(styles.valid);
                clearErrorMessage();
                handleEnabledSubmit(true);
            } else if (invalidInput) {
                setValidationStyle(styles.invalid);
                setErrorMessage();
                handleEnabledSubmit(false);
            }
        }, [value, compareValue, skipValidation]);

        useEffect(() => {
            switch (inputType) {
                case "firstname":
                    setLabel(tg.firstName);
                    setMaxLength(50);
                    break;
                case "lastname":
                    setLabel(tg.surname);
                    setMaxLength(150);
                    break;
                case "phone":
                    setLabel(tg.phone);
                    setMaxLength(50);
                    break;
                case "email":
                    setAutoComplete("username");
                    setLabel(tg.eMail);
                    setMaxLength(50);
                    break;
                case "current-password":
                    setAutoComplete("current-password");
                    setLabel(tg.password);
                    setTypeField("password");
                    setMaxLength(32);
                    break;
                case "new-password":
                    setAutoComplete("new-password");
                    setLabel(t.newPassword);
                    setTypeField("password");
                    setMaxLength(32);
                    break;
                case "confirm-password":
                    setAutoComplete("off");
                    setLabel(t.confirmPassword);
                    setTypeField("password");
                    setMaxLength(32);
                    break;
                case "socialSecurityNumber":
                    setPlaceholderField(
                        isFinanceLink
                            ? tg.social_security_number_finnish_format
                            : tg.social_security_number_swedish_format,
                    );
                    setAutoComplete("off");
                    setLabel(tg.socialSecurityNumber);
                    setMaxLength(
                        isFinanceLink
                            ? MAX_LENGTH_FINNISH_SSN
                            : MAX_LENGTH_SWEDISH_SSN,
                    );
                    break;
                case "email-or-customer-nbr":
                    setAutoComplete("username");
                    setLabel(t.email);
                    setMaxLength(50);
                    break;
                case "customer-nbr":
                    setPlaceholderField(`${tg.abbrEG} 12345`);
                    setAutoComplete("username");
                    setLabel(tg.customerNumber);
                    setMaxLength(50);
                    break;
                case "search-customer":
                    setPlaceholderField(t.invoiceNumberOrCustomer);
                    setMaxLength(50);
                    setTypeField("search");
                    break;
                case "account-category":
                    setMaxLength(4);
                    break;
                default:
                    setAutoComplete("off");
            }
        }, [isFinanceLink, inputType, t, tg]);

        // TODO (later) the parameters could be improved later/when changing to Typescript
        const handleChanged = (event) => {
            let formattedSSN = undefined;
            if (inputType === "socialSecurityNumber") {
                const value = event?.target.value;
                formattedSSN = isFinanceLink
                    ? formatFinishSSN(value)
                    : formatSwedishSSN(value);
            }
            onChange(event, formattedSSN);
        };

        const inputField = (
            <div className={styles.inputContainer}>
                <input
                    ref={ref}
                    id={uid}
                    type={typeField}
                    value={value}
                    placeholder={placeholderField}
                    onChange={handleChanged}
                    className={`${styles.input} ${
                        !label && isDisabled ? styles.disabled : ""
                    } ${className || ""}`}
                    autoComplete={autoComplete}
                    onFocus={() => setIsFocused(true)}
                    onBlur={() => setIsFocused(false)}
                    style={{ width }}
                    maxLength={maxLength}
                    onClick={onClick}
                    onKeyDown={onKeyDown}
                    disabled={isDisabled}
                    readOnly={isReadOnly}
                    name={name}
                />
                {skipValidation !== true &&
                    !isFocused &&
                    invalidInput &&
                    !noInput && (
                        <div
                            className={`${styles.svgContainer} ${styles.error}`}
                        >
                            <InlineSvg src="/input-error.svg" />
                        </div>
                    )}
                {/* {!isFocused && (validInput && !noInput && <div className={`${styles.svgContainer} ${styles.success}`}><InlineSvg src="/input-success.svg" /></div>)} */}
            </div>
        );

        return label ? (
            <label
                errormsg={errorMsg}
                htmlFor={uid}
                className={`${styles.label} ${validationStyle || ""} ${
                    isFocused ? styles.focused : ""
                } ${isDisabled === true ? styles.disabled : ""} ${
                    labelClassName || ""
                }`}
            >
                <span>{label}</span>
                {inputField}
            </label>
        ) : (
            inputField
        );
    },
);

InputText.propTypes = {
    value: PropTypes.string,
    onChange: PropTypes.func,
    width: PropTypes.string,
    className: PropTypes.string,
    inputType: PropTypes.string,
    label: PropTypes.string,
    labelClassName: PropTypes.string,
    placeholder: PropTypes.string,
    setEnabledSubmit: PropTypes.func,
    isRequired: PropTypes.bool,
    compareValue: PropTypes.string,
    onClick: PropTypes.func,
    onKeyDown: PropTypes.func,
    isReadOnly: PropTypes.bool,
    isDisabled: PropTypes.bool,
    skipValidation: PropTypes.bool,
};

export default InputText;
